/**
 * The workspace URL parameter.
 *
 * 🔴 IT IS `?w=`, AND IT IS DELIBERATELY NOT `?account=`.
 *
 * `?account=` already ships at four call sites with three incompatible
 * encodings --- a bare `user_connections.id` (`FeedPage.tsx`), a `resource.key`
 * (`GoogleCampaignAuthoringPage.tsx`), and a composite `connectionId:resourceId`
 * (`MetaCampaignAuthoringPage.tsx`, plus `GoogleAdsCampaignsPage.tsx`). All four
 * mean the same NOUN: "which connected ad/social account."
 *
 * A workspace is a different noun. Overloading `?account=` would add the first
 * genuine *semantic* ambiguity on top of what is currently only an *encoding*
 * inconsistency. It would also make `/publishing/feed?w=<brand>&account=<connection>`
 * impossible to express, and that URL is the normal case: this workspace, that
 * connected account.
 */
export const WORKSPACE_URL_PARAM = 'w';

/**
 * Read the requested workspace from a query string.
 *
 * Returns `null` when absent or malformed. Callers must treat a non-null value
 * as a *request*, never as truth: it is validated server-side against the
 * account's kits, and an unrecognised id must surface an explicit picker rather
 * than silently falling back to the stored pointer. A silent fallback is how
 * two pickers end up disagreeing on screen.
 */
export function readWorkspaceFromUrl(search: string): string | null {
  const raw = new URLSearchParams(search).get(WORKSPACE_URL_PARAM);
  if (!raw) return null;
  const trimmed = raw.trim();
  return UUID_RE.test(trimmed) ? trimmed : null;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Carry `?w=` from a page's current address into an in-app `navigate()` target.
 *
 * A cross-page redirect that hands React Router a bare path (`/campaigns/meta/new`)
 * drops whichever workspace the reader was in — it then survives only in the
 * workspace switcher's own pointer, not the URL, until the next hard load or
 * explicit switch. This is a `navigate()`-time concern, not a render-time one
 * (`readWorkspaceFromUrl` above already covers reading the param back out on
 * the far side), so it lives beside it rather than being re-derived with
 * `URLSearchParams` at every call site that redirects.
 *
 * A `target` that already carries its own query string keeps it — `?w=` is
 * added to it, never used to replace it. A `currentSearch` with no resolvable
 * `?w=` leaves `target` untouched.
 */
export function withWorkspaceParam(target: string, currentSearch: string): string {
  const workspaceId = readWorkspaceFromUrl(currentSearch);
  if (!workspaceId) return target;
  const [path, existingQuery = ''] = target.split('?');
  const params = new URLSearchParams(existingQuery);
  params.set(WORKSPACE_URL_PARAM, workspaceId);
  return `${path}?${params.toString()}`;
}

export function withoutWorkspaceParam(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(WORKSPACE_URL_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : '';
}
